import type { ChartProps } from '@/components/atoms/Chart';
import type { DistributedAsset } from '@/components/molecules/AssetsDistributionChart';
import type { Options } from 'highcharts';
import { type FC, useMemo } from 'react';
import { Box, Flex, Skeleton, Text } from '@eqi-investimentos/react';
import { colors as colorsTokens } from '@eqi-investimentos/tokens';
import { useColorMode } from '@chakra-ui/react';
import { Chart } from '@/components/atoms/Chart/';
import { generateArrayColorsTokens } from '@/utils/chart';
import { formatNumberToPTBRCurrency } from '@/utils/format';
import { CHART_COLORS } from '@/themes/chart';
import { chartOptions } from './options';
export const MyWalletCategoriesChart: FC<MyWalletCategoriesChartProps> = ({
  assets,
  isMobile,
  colors = CHART_COLORS,
  isLoading = false
}) => {
  const {
    colorMode
  } = useColorMode();
  const isDarkMode = colorMode === 'dark';
  const currentTheme: 'light' | 'dark' = isDarkMode ? 'dark' : 'light';
  const data = useMemo(() => {
    if (!assets || assets.length === 0) {
      return [];
    }
    return assets.filter(asset => asset.total > 0).map(({
      name,
      total
    }) => [name, total]);
  }, [assets]);
  const series: ChartProps['series'] = useMemo(() => [{
    type: 'pie',
    data,
    name: 'Categorias'
  }], [data]);
  const colorsToken = useMemo(() => generateArrayColorsTokens(colors, isDarkMode), [colors, isDarkMode]);
  const options: Options = useMemo(() => ({
    ...chartOptions,
    chart: {
      ...chartOptions.chart,
      height: isMobile ? 120 : 150,
      width: isMobile ? 120 : 150
    },
    plotOptions: {
      ...chartOptions.plotOptions,
      pie: {
        ...chartOptions.plotOptions?.pie,
        size: isMobile ? '120px' : '130px'
      }
    },
    tooltip: {
      ...chartOptions.tooltip,
      formatter: function () {
        const value = formatNumberToPTBRCurrency(this.point.y!);
        const brandColor = CHART_COLORS[this.point.index];
        const color = (colorsTokens as unknown as Record<string, Record<string, string>>)[currentTheme][brandColor];
        return `
            <div>
              <div style="display:flex;align-items:center;gap:0.25rem;"><div style="height:0.5rem;width:0.5rem;background-color:${color};"></div><p><b>${this.point.name}</b></p></div>
              <p>${value}</p>
            </div>`;
      }
    },
    colors: colorsToken
  }), [isMobile, currentTheme, colorsToken]);

  // Don't render chart if there's no data
  if (isLoading) {
    return <Flex className="container__spinner" justifyContent="center" alignItems="center" w="8.75rem" h="8.75rem" borderRadius="full">
        <Skeleton w="8.125rem" h="8.125rem" borderRadius="5em" />
      </Flex>;
  }

  // Return empty state if no valid data
  if (!data || data.length === 0) {
    return <Box minH="10.625rem" display="flex" alignItems="center" justifyContent="center">
        <Flex w="8.75rem" h="8.75rem" borderRadius="full" bg="surface.subtle" alignItems="center" justifyContent="center">
          <Text fontSize="sm" color="content.tertiary" textAlign="center">
            Sem dados
          </Text>
        </Flex>
      </Box>;
  }
  return <Box minH="10.625rem" data-sentry-element="Box" data-sentry-component="MyWalletCategoriesChart" data-sentry-source-file="index.tsx">
      <Chart options={options} series={series} data-sentry-element="Chart" data-sentry-source-file="index.tsx" />
    </Box>;
};
export type MyWalletCategoriesChartProps = {
  assets: Omit<Required<DistributedAsset>, 'subCategory'>[];
  isMobile: boolean;
  colors?: string[];
  isLoading?: boolean;
};